// src/lib/productionDatabase.js
// Session 79.7, Issue #507.
//
// IN PLAIN TERMS: one question, answered in one place — "is this copy of Stede
// talking to the real company database, or to the practice one?"
//
// ── WHY THIS FILE EXISTS RATHER THAN THE ANSWER LIVING WHERE IT IS USED ──
//
// Two unrelated features need it and they run on different machines. The
// staging mail guard (#506) runs SERVER-SIDE in a Cloudflare Pages Function and
// uses it to decide whether to divert email away from real people. Email link
// building (#507) runs CLIENT-SIDE in the browser and uses it to decide whether
// a button should point at the canonical host or at the deployment that sent
// the message.
//
// Copying the rule into both is the shape this project has been bitten by
// repeatedly: a permission rule that lived in three places leaked from the one
// nobody was editing, and the standing lesson from that is that the third copy
// is never in the file you have open. So it lives here once and both import it.
//
// ── WHY THIS MODULE HOLDS NOTHING BUT A CONSTANT AND A PURE FUNCTION ──
//
// It is imported by a Pages Function, and a Worker runtime has no `process` and
// no `window`. `src/lib/appUrl.js` reads `process.env` at module scope, so
// importing THAT into a Function would throw at load rather than at call — the
// worst kind of failure, because it takes the whole endpoint down before any
// request is handled. Keeping this file free of both globals is what makes it
// safe on both runtimes, and it is the reason the predicate was not simply left
// in appUrl.js.
//
// Functions importing from `src/lib/` is an established pattern here:
// `functions/quickbooks-bill.js` already does it. The reverse would break the
// app build, because Create React App refuses imports from outside `src/`.

// The production project ref. Hardcoded on purpose and it is not a secret — a
// Supabase project ref appears in plain sight in every anon JWT, and
// `src/lib/supabase.js` already carries this same value as its fallback URL.
// Matching on the ref rather than on the whole URL means a trailing slash, a
// custom domain or a protocol change cannot accidentally change the answer.
export const PRODUCTION_PROJECT_REF = 'pyenbriacaivrnkwitqu';

// AN UNKNOWN DATABASE COUNTS AS PRODUCTION, deliberately, and both callers
// depend on that direction.
//
// For the mail guard, guessing "staging" wrongly reroutes a whole company's
// mail to one inbox — a silent outage — while guessing "production" wrongly
// lets a test bounce off `example.com`, which is a nuisance.
//
// For email links, guessing "staging" wrongly puts a dying preview URL in mail
// sent to real staff, while guessing "production" wrongly means a staging
// email keeps today's behaviour. One breaks something; the other changes
// nothing.
//
// Both failure directions point the same way, which is why one predicate can
// serve both.
export function isNonProductionDatabase(supabaseUrl) {
  if (!supabaseUrl) return false;                 // unknown => treat as production
  // LOWERCASED BEFORE COMPARING, and this is not tidiness — it was a real defect
  // caught by the mail guard's own test before it shipped, and it failed in the
  // catastrophic direction. A host name is case-insensitive, so
  // `HTTPS://PYENBRIACAIVRNKWITQU.SUPABASE.CO` addresses production exactly as
  // the lowercase form does; a case-sensitive `includes` did not recognise it,
  // concluded "not production", and ARMED THE GUARD ON PRODUCTION.
  const s = String(supabaseUrl).toLowerCase();
  if (s.includes(PRODUCTION_PROJECT_REF)) return false;
  // A ref is 20 lowercase letters. Anything that does not look like a Supabase
  // project URL at all is not evidence of staging, so it is refused too.
  return /https?:\/\/[a-z]{20}\.supabase\./.test(s);
}
